/**
 * aethereum status (alias: aethereum doctor) — self-diagnosis for auto mode.
 *
 * Answers "is it actually on?": resolves the room token, pings the hosted
 * context endpoint (token valid? what does the room see?), and checks that the
 * Claude Code hooks + MCP server are wired in this project. Read-only; never
 * mutates anything. Exits 0 when healthy, 1 when something needs attention.
 */

import { readFileSync, existsSync } from "node:fs";
import { join } from "node:path";
import { loadAutoConfig } from "../hive/auto.js";
import { hiveOnPath, hiveInvocation, mergeClaudeSettings, mergeMcpJson } from "./init.js";
import { supportsColor, colorizeStatusLine, heading, success } from "./splash.js";
import { corruptConfigBackups, resolveProject } from "../hive/config-store.js";

export interface StatusFacts {
  /** AETHEREUM_AUTO=off in the environment. */
  autoOff: boolean;
  /** A token was resolved (env or ~/.aethereum/config.json). */
  configured: boolean;
  url?: string;
  /**
   * Context endpoint outcome:
   *   "ok"           — 200, token valid
   *   "redirect"     — the request was redirected (apex → www) and the auth header
   *                    was dropped en route; looks like a bad token but is a URL bug
   *   "unauthorized" — 401, the server never saw a usable bearer
   *   "rejected"     — 403, token revoked or wrong room
   *   "unreachable"  — network / non-auth failure
   */
  reach: "ok" | "redirect" | "unauthorized" | "rejected" | "unreachable" | "n/a";
  contracts?: number;
  presence?: number;
  alerts?: number;
  hooksInstalled: boolean;
  /** Hook events NOT wired in .claude/settings.json (of the six aethereum needs). */
  hookMissing?: string[];
  /** The hook command's binary/script path no longer resolves on this machine. */
  hookPathBroken?: boolean;
  /** .mcp.json carries a DIFFERENT token than ~/.aethereum/config.json. */
  tokenDrift?: boolean;
  mcpInstalled: boolean;
  onPath: boolean;
  /** ~/.aethereum/config.json was unreadable and got backed up here (distinct
   *  from "no token found": the config existed but was corrupt). */
  corruptBackup?: string;
}

const OK = "[ok] ";
const WARN = "[!]  ";
const BAD = "[x]  ";

/** Render the diagnosis as a checklist. Pure, so it is unit-testable. */
export function buildStatusReport(f: StatusFacts): { lines: string[]; healthy: boolean } {
  const lines: string[] = [];
  let healthy = true;

  if (f.autoOff) {
    lines.push(`${WARN}auto mode is disabled (AETHEREUM_AUTO=off). Unset it to turn Aethereum back on.`);
  }

  if (!f.configured) {
    if (f.corruptBackup) {
      lines.push(
        `${BAD}your ~/.aethereum/config.json was unreadable and has been backed up to ${f.corruptBackup}.`,
      );
      lines.push(
        `     Recover: 'aethereum reconnect --token <key>' (keeps your room) or re-run 'aethereum init'.`,
      );
    } else {
      lines.push(`${BAD}no room token found. Run 'aethereum init --token <key>' (get one from the dashboard).`);
    }
    return { lines, healthy: false };
  }
  lines.push(`${OK}room token found  (${f.url})`);

  if (f.reach === "ok") {
    lines.push(`${OK}hosted API reachable, token valid`);
    lines.push(
      `     room sees ${f.contracts ?? 0} contract(s), ${f.presence ?? 0} teammate(s) live` +
        (f.alerts ? `, ${f.alerts} new alert(s)` : ""),
    );
  } else if (f.reach === "redirect") {
    lines.push(
      `${BAD}the API redirected and dropped the auth header (apex -> www). Re-run 'aethereum init' to use the canonical URL (https://www.aethereum.dev).`,
    );
    healthy = false;
  } else if (f.reach === "unauthorized") {
    lines.push(`${BAD}the server did not accept the token (401). Re-run 'aethereum init --token <key>'.`);
    healthy = false;
  } else if (f.reach === "rejected") {
    lines.push(`${BAD}token was rejected (revoked or wrong room). Reconnect from the dashboard.`);
    healthy = false;
  } else {
    lines.push(`${WARN}could not reach the hosted API. Context still degrades silently; check your network.`);
    healthy = false;
  }

  if (f.hooksInstalled && !(f.hookMissing?.length) && !f.hookPathBroken) {
    lines.push(`${OK}Claude Code hooks installed in this project (all six events)`);
  } else if (!f.hooksInstalled) {
    lines.push(`${BAD}no hooks in .claude/settings.json here. Run 'aethereum doctor --fix' (or 'aethereum init').`);
    healthy = false;
  } else {
    if (f.hookMissing?.length) {
      lines.push(
        `${BAD}hooks partially installed: missing ${f.hookMissing.join(", ")}. Run 'aethereum doctor --fix' to repair.`,
      );
      healthy = false;
    }
    if (f.hookPathBroken) {
      lines.push(
        `${BAD}the hook command's path no longer resolves on this machine. Run 'aethereum doctor --fix' to rewrite it.`,
      );
      healthy = false;
    }
  }

  if (f.mcpInstalled) {
    lines.push(`${OK}.mcp.json wired to the hosted MCP`);
  } else {
    lines.push(`${WARN}no aethereum server in .mcp.json here. Run 'aethereum doctor --fix' (or 'aethereum init') to add it.`);
  }

  if (f.tokenDrift) {
    lines.push(
      `${BAD}.mcp.json carries a different token than ~/.aethereum/config.json (drift). Run 'aethereum doctor --fix' to align them.`,
    );
    healthy = false;
  }

  if (!f.onPath) {
    lines.push(`${WARN}'aethereum' is not on your PATH. Hooks use a full path; that works, but 'npm i -g aethereum' is cleaner.`);
  }

  return { lines, healthy };
}

// ── hooks / mcp detection (read-only) ────────────────────────────────────────

/** The six hook subcommands `aethereum init` wires (must match init.ts HOOK_TYPES). */
const HOOK_SUBS = ["session-start", "prompt", "post-tool-use", "stop", "subagent-start", "subagent-stop"] as const;

export interface HookInspection {
  installed: boolean;
  /** hook subcommands with no matching entry in .claude/settings.json. */
  missing: string[];
  /** true when a hook command's binary/script path cannot resolve anymore. */
  pathBroken: boolean;
}

/** True when the leading binary/script of a hook command still resolves: `npx`
 *  and a bare `aethereum` (when on PATH) are fine; an absolute node+script pair
 *  must still exist on disk. Exported for tests. */
export function hookCommandResolves(command: string, onPath: () => boolean = hiveOnPath): boolean {
  const parts = command.trim().split(/\s+/);
  const first = parts[0] ?? "";
  if (first === "npx") return true;
  if (first === "aethereum") return onPath();
  // Absolute invocation: `<node> <script> hook <sub>` — both paths must exist.
  if (first.startsWith("/") || /^[A-Za-z]:[\\/]/.test(first)) {
    if (!existsSync(first)) return false;
    const second = parts[1] ?? "";
    if (second && !second.startsWith("-") && second !== "hook" && (second.startsWith("/") || /^[A-Za-z]:[\\/]/.test(second))) {
      return existsSync(second);
    }
    return true;
  }
  return true; // unknown shape: don't cry wolf
}

/** Deep hook inspection: ALL SIX hook events must be wired (the old check looked
 *  at only three, so a half-installed block read as healthy) and the invocation
 *  path must still resolve. Exported for tests. */
export function inspectHooks(cwd: string, onPath: () => boolean = hiveOnPath): HookInspection {
  let raw: string;
  try {
    raw = readFileSync(join(cwd, ".claude", "settings.json"), "utf8");
  } catch {
    return { installed: false, missing: [...HOOK_SUBS], pathBroken: false };
  }
  const missing = HOOK_SUBS.filter((sub) => !new RegExp(`hook\\s+${sub}`).test(raw));
  const installed = missing.length < HOOK_SUBS.length;
  // Path resolution: pull every `"command": "..."` that mentions our hook and
  // verify its leading binary still resolves.
  let pathBroken = false;
  try {
    const settings = JSON.parse(raw) as {
      hooks?: Record<string, Array<{ hooks?: Array<{ command?: string }> }> | undefined>;
    };
    const commands: string[] = [];
    for (const groups of Object.values(settings.hooks ?? {})) {
      for (const g of groups ?? []) {
        for (const h of g.hooks ?? []) {
          if (h.command && /\bhook\s+\S+/.test(h.command)) commands.push(h.command);
        }
      }
    }
    pathBroken = commands.some((c) => !hookCommandResolves(c, onPath));
  } catch {
    pathBroken = false; // unparseable settings: the missing[] signal is enough
  }
  return { installed, missing: installed ? missing : [...HOOK_SUBS], pathBroken };
}

/** True when .mcp.json's aethereum bearer differs from the config-store token. */
export function detectTokenDrift(cwd: string, configToken: string | undefined): boolean {
  if (!configToken) return false;
  try {
    const raw = readFileSync(join(cwd, ".mcp.json"), "utf8");
    const json = JSON.parse(raw) as {
      mcpServers?: Record<string, { headers?: Record<string, string> }>;
    };
    const auth = json.mcpServers?.["aethereum"]?.headers?.["Authorization"];
    if (!auth) return false;
    const mcpToken = auth.replace(/^Bearer\s+/i, "").trim();
    return Boolean(mcpToken) && mcpToken !== configToken;
  } catch {
    return false;
  }
}

function mcpInstalled(cwd: string): boolean {
  try {
    const raw = readFileSync(join(cwd, ".mcp.json"), "utf8");
    const json = JSON.parse(raw) as { mcpServers?: Record<string, unknown> };
    return Boolean(json.mcpServers && "aethereum" in json.mcpServers);
  } catch {
    return false;
  }
}

async function probeContext(
  url: string,
  token: string,
  fetchImpl: typeof fetch,
): Promise<Pick<StatusFacts, "reach" | "contracts" | "presence" | "alerts">> {
  try {
    const res = await fetchImpl(`${url}/api/sync/context?peek=1`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(5_000),
    });
    // A followed redirect strips Authorization, so the call lands unauthenticated.
    // Surface that as its own diagnosis instead of blaming the token.
    if (res.redirected && (res.status === 401 || res.status === 403)) return { reach: "redirect" };
    if (res.status === 401) return { reach: "unauthorized" };
    if (res.status === 403) return { reach: "rejected" };
    if (!res.ok) return { reach: "unreachable" };
    const body = (await res.json()) as { contracts?: number; presence?: number; alerts?: unknown[] };
    return {
      reach: "ok",
      contracts: body.contracts ?? 0,
      presence: body.presence ?? 0,
      alerts: Array.isArray(body.alerts) ? body.alerts.length : 0,
    };
  } catch {
    return { reach: "unreachable" };
  }
}

export interface StatusOptions {
  cwd?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
  /** Emit the stable machine-readable shape instead of the checklist (v4-S12). */
  json?: boolean;
  /** Home override (tests): config + session index resolution. */
  home?: string;
  /** Repair mode: re-run the idempotent init writers before reporting. */
  fix?: boolean;
}

/** `aethereum doctor --fix`: re-run the idempotent writers from init.ts so a
 *  broken/partial hook block or a drifted .mcp.json is repaired in place. Never
 *  throws; returns what happened as printable lines. Exported for tests. */
export function fixProject(cwd: string, home?: string): { lines: string[]; ok: boolean } {
  const lines: string[] = [];
  let project: ReturnType<typeof resolveProject>;
  try {
    project = resolveProject(cwd, home);
  } catch {
    project = undefined;
  }
  if (!project) {
    return {
      lines: [
        `${WARN}nothing to repair with: no room token for this directory. Run 'aethereum init' (or 'aethereum reconnect --token <key>').`,
      ],
      ok: false,
    };
  }
  try {
    mergeMcpJson(cwd, project.url.replace(/\/$/, ""), project.token);
    lines.push(`${OK}.mcp.json rewritten from the configured room token`);
  } catch (err) {
    lines.push(`${WARN}could not rewrite .mcp.json (${err instanceof Error ? err.message : String(err)})`);
  }
  try {
    const { added } = mergeClaudeSettings(cwd, hiveInvocation());
    lines.push(
      `${OK}.claude/settings.json hooks ${added.length ? `added: ${added.join(", ")}` : "refreshed (all six events)"}`,
    );
  } catch (err) {
    lines.push(
      `${WARN}could not rewrite .claude/settings.json (${err instanceof Error ? err.message : String(err)})`,
    );
  }
  return { lines, ok: true };
}

/**
 * The stable machine shape behind `aethereum status --json` (consumed by the
 * SwiftBar plugin in docs/statusline/). Every field beyond `configured` is
 * fail-soft: null when unknown or unreachable, never an exception.
 */
export interface StatusJson {
  configured: boolean;
  room: string | null;
  member: string | null;
  url?: string;
  alerts: number | null;
  presence: number | null;
  sessionsToday: number | null;
  /** Estimated USD across today's local sessions; estimates, may be null. */
  estCostToday: number | null;
}

/** Build the --json payload. Exported for tests; every failure degrades to null. */
export async function buildStatusJson(options: StatusOptions = {}): Promise<StatusJson> {
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const cfg = loadAutoConfig(cwd, { ...env, AETHEREUM_AUTO: "" }, options.home);

  let room: string | null = null;
  let member: string | null = null;
  try {
    const { resolveProject } = await import("../hive/config-store.js");
    room = resolveProject(cwd, options.home)?.roomId ?? null;
  } catch {
    room = null;
  }
  try {
    const { machineHandle } = await import("../hive/machine-handle.js");
    member = machineHandle();
  } catch {
    member = null;
  }

  let probe: Pick<StatusFacts, "reach" | "contracts" | "presence" | "alerts"> = { reach: "n/a" };
  if (cfg) probe = await probeContext(cfg.url, cfg.token, options.fetchImpl ?? fetch);

  let sessionsToday: number | null = null;
  let estCostToday: number | null = null;
  try {
    const { loadSessionSummaries } = await import("./sessions.js");
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    const rows = (await loadSessionSummaries({ home: options.home })).filter(
      (r) => (r.endedAt ?? r.startedAt ?? 0) >= midnight.getTime(),
    );
    sessionsToday = rows.length;
    let cost = 0;
    let any = false;
    for (const r of rows) {
      if (r.costUsd !== null) {
        cost += r.costUsd;
        any = true;
      }
    }
    estCostToday = any ? Number(cost.toFixed(4)) : null;
  } catch {
    sessionsToday = null;
    estCostToday = null;
  }

  return {
    configured: Boolean(cfg),
    room,
    member,
    ...(cfg ? { url: cfg.url } : {}),
    alerts: probe.reach === "ok" ? (probe.alerts ?? 0) : null,
    presence: probe.reach === "ok" ? (probe.presence ?? 0) : null,
    sessionsToday,
    estCostToday,
  };
}

export async function runStatus(options: StatusOptions = {}): Promise<void> {
  if (options.json) {
    // Machine mode: one stable JSON object, always exit 0 (fail-soft consumers).
    try {
      console.log(JSON.stringify(await buildStatusJson(options)));
    } catch {
      console.log(
        JSON.stringify({
          configured: false,
          room: null,
          member: null,
          alerts: null,
          presence: null,
          sessionsToday: null,
          estCostToday: null,
        }),
      );
    }
    return;
  }

  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const fetchImpl = options.fetchImpl ?? fetch;
  const color = supportsColor(env, Boolean(process.stdout.isTTY));

  // Repair mode first, so the report below reflects the repaired state.
  if (options.fix) {
    const fixed = fixProject(cwd, options.home);
    console.log("\n" + heading("Aethereum doctor --fix", color) + "\n");
    for (const line of fixed.lines) console.log("  " + colorizeStatusLine(line, color));
  }

  const autoOff = (env["AETHEREUM_AUTO"] ?? "").toLowerCase() === "off";
  // loadAutoConfig returns null when AETHEREUM_AUTO=off; probe the underlying config
  // regardless so status still reports what is wired up.
  const cfg = loadAutoConfig(cwd, { ...env, AETHEREUM_AUTO: "" }, options.home);

  let probe: Pick<StatusFacts, "reach" | "contracts" | "presence" | "alerts"> = { reach: "n/a" };
  if (cfg) probe = await probeContext(cfg.url, cfg.token, fetchImpl);

  const hooks = inspectHooks(cwd);
  let projectToken: string | undefined;
  try {
    projectToken = resolveProject(cwd, options.home)?.token;
  } catch {
    projectToken = undefined;
  }

  const facts: StatusFacts = {
    autoOff,
    configured: Boolean(cfg),
    url: cfg?.url,
    ...probe,
    hooksInstalled: hooks.installed,
    hookMissing: hooks.missing,
    hookPathBroken: hooks.pathBroken,
    tokenDrift: detectTokenDrift(cwd, projectToken),
    mcpInstalled: mcpInstalled(cwd),
    onPath: hiveOnPath(),
    // Distinct "config was corrupt" state: no config resolved AND a backup exists.
    corruptBackup: !cfg ? corruptConfigBackups(options.home)[0] : undefined,
  };

  const { lines, healthy } = buildStatusReport(facts);
  console.log("\n" + heading("Aethereum status", color) + "\n");
  for (const line of lines) console.log("  " + colorizeStatusLine(line, color));
  console.log(healthy ? "\n  " + success("All set. Your agents are sharing context.", color) + "\n" : "");
  if (!healthy) process.exit(1);
}
